import React, { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { useGetAlertsQuery } from '#/services/developerApi';
import { ALERTS_POLLING_INTERVAL } from '#/transit-tracker/constants';
import useSidePanelToggle from '#/user/hooks/useSidePanelToggle';
import * as Styled from './styled';

function DetailNavHeader({ content, onBack, onClose, isHome }) {
  const { data } = useGetAlertsQuery(undefined, {
    pollingInterval: ALERTS_POLLING_INTERVAL,
  });
  const { sidePanelIsMinimized, toggleSidePanelMinMax } = useSidePanelToggle();
  const hasSystemWideAlert = data ? data.hasSystemWideAlert : false;
  const { mobileMode, alertBannerIsVisible } = useSelector(s => s.user);
  const hideMapOnMobile = useSelector(s => s.map.hideMapOnMobile);
  const intl = useIntl();
  const pageTitleRef = useRef(null);
  const [hasOverflowingChildren, setHasOverflowingChildren] = useState(false);
  const [offsetWidth, setOffsetWidth] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);

  const ChevronButton = sidePanelIsMinimized
    ? Styled.ExpandSidePanelButton
    : Styled.MinSidePanelButton;

  const checkOverflow = useCallback(() => {
    if (pageTitleRef.current) {
      setOffsetWidth(pageTitleRef.current.offsetWidth);
      setScrollWidth(pageTitleRef.current.scrollWidth);
      setHasOverflowingChildren(offsetWidth < scrollWidth);
    }
  }, [offsetWidth, scrollWidth]);

  useEffect(() => {
    window.addEventListener('resize', checkOverflow);
    setTimeout(() => {
      checkOverflow();
    }, 500);
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [offsetWidth, scrollWidth, checkOverflow]);

  return (
    <Styled.DetailNavHeaderWrapper
      alertBannerIsVisible={alertBannerIsVisible}
      hasSystemWideAlert={hasSystemWideAlert}
      hideMapOnMobile={hideMapOnMobile}
      mobileMode={mobileMode}
      minimized={sidePanelIsMinimized}
      onClick={() => (sidePanelIsMinimized ? toggleSidePanelMinMax() : null)}
    >
      {!sidePanelIsMinimized && (
        <Styled.BackButton
          onClick={onBack}
          title={intl.formatMessage({
            id: 'detailview.backbtn_acc',
          })}
          hide={!onBack}
        >
          <Styled.ArrowBackIcon />
        </Styled.BackButton>
      )}
      <Styled.DetailNavHeaderContent
        minimized={sidePanelIsMinimized}
        isHome={isHome}
        mobileMode={mobileMode}
        hideClose={!onClose}
        onClick={() =>
          !sidePanelIsMinimized && !mobileMode && toggleSidePanelMinMax()
        }
        disabled={mobileMode}
      >
        <Styled.PageTitle
          ref={pageTitleRef}
          hideClose={!onClose}
          mobileMode={mobileMode}
          textOverflow={hasOverflowingChildren}
        >
          {isHome ? <FormattedMessage id="detailview.trip_tools" /> : content}
        </Styled.PageTitle>
      </Styled.DetailNavHeaderContent>
      <Styled.MinMaxCloseWrapper>
        {!mobileMode && (
          <ChevronButton
            onClick={() => !sidePanelIsMinimized && toggleSidePanelMinMax()}
          >
            <Styled.MinMaxChevronIcon $minimized={sidePanelIsMinimized} />
          </ChevronButton>
        )}
        {!sidePanelIsMinimized && (
          <Styled.CloseButton
            onClick={onClose}
            title={intl.formatMessage({
              id: 'detailview.closebtn_acc',
            })}
            hide={!onClose}
          >
            <Styled.CloseIcon />
          </Styled.CloseButton>
        )}
      </Styled.MinMaxCloseWrapper>
    </Styled.DetailNavHeaderWrapper>
  );
}

DetailNavHeader.propTypes = {
  /** Content to display in the center location */
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /** Invoked when clicking the back button, if null back button is hidden */
  onBack: PropTypes.func,
  /** Invoked when clicking the close button, if null close button is hidden */
  onClose: PropTypes.func,
  /** True if the current page route is the home page */
  isHome: PropTypes.bool,
};

DetailNavHeader.defaultProps = {
  content: '',
  onBack: null,
  onClose: null,
  isHome: undefined,
};

export default DetailNavHeader;
